import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendar,
  FaStethoscope,
  FaNotesMedical,
  FaPlus,
  FaTimes,
  FaArrowLeft,
  FaHistory,
  FaFileUpload,
  FaFileAlt,
  FaDownload,
  FaTrash,
  FaMapMarkerAlt,
  FaBriefcase,
  FaSpinner,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../utils/apiConfig";
import { formatAppointmentDate } from "../utils/dateUtils";
import "./PatientProfile.css";

const PatientProfile = ({ patient, onClose, onRefresh }) => {
  const [notes, setNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [consultationDate, setConsultationDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Reports state
  const [reports, setReports] = useState([]);
  const [showUploadReport, setShowUploadReport] = useState(false);
  const [reportFile, setReportFile] = useState(null);
  const [reportName, setReportName] = useState("");
  const [reportType, setReportType] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [uploadDate, setUploadDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (patient && patient.notes) {
      setNotes(patient.notes);
    } else {
      setNotes([]);
    }

    if (patient && patient.reports) {
      setReports(patient.reports);
    } else {
      setReports([]);
    }
  }, [patient]);

  // Safety check - if no patient data, show loading or error
  if (!patient) {
    return (
      <div className="patient-profile-overlay">
        <div className="patient-profile-container">
          <div className="loading-message">
            <p>Loading patient information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Debug: Log patient data
  console.log("Patient data received:", patient);
  console.log("Patient fields:", {
    name: patient.name,
    email: patient.email,
    phone: patient.phone,
    age: patient.age,
    sex: patient.sex,
    address: patient.address,
    occupation: patient.occupation,
    totalAppointments: patient.totalAppointments,
    services: patient.services,
    appointments: patient.appointments,
    notes: patient.notes,
  });

  // Debug: Check if fields exist
  console.log("Field existence check:", {
    hasName: !!patient.name,
    hasEmail: !!patient.email,
    hasPhone: !!patient.phone,
    hasAge: !!patient.age,
    hasSex: !!patient.sex,
    hasAddress: !!patient.address,
    hasOccupation: !!patient.occupation,
    hasTotalAppointments: !!patient.totalAppointments,
    hasServices: !!patient.services,
    hasAppointments: !!patient.appointments,
    hasNotes: !!patient.notes,
  });

  // Debug: Show all available keys
  console.log("All available patient keys:", Object.keys(patient));

  // Debug: Check notes specifically
  console.log("Notes data:", {
    notes: patient.notes,
    notesType: typeof patient.notes,
    notesLength: patient.notes ? patient.notes.length : "undefined",
    notesArray: Array.isArray(patient.notes),
  });

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) {
      setError("Please enter a note");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_NOTES),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patientIdentifier: patient.email || patient.phone,
            note: newNote.trim(),
            consultationDate,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // Use the note data returned from the server instead of creating our own
        const noteToAdd = {
          timestamp: data.note.timestamp || new Date().toISOString(),
          patient_id: data.note.patient_id,
          consultation_date: data.note.consultation_date || consultationDate,
          note: data.note.note || newNote.trim(),
          // Also include the old format for backward compatibility
          Timestamp: data.note.timestamp || new Date().toISOString(),
          PatientID: data.note.patient_id,
          ConsultationDate: data.note.consultation_date || consultationDate,
          Note: data.note.note || newNote.trim(),
        };

        // Update notes state smoothly without causing flicker
        setNotes((prevNotes) => [noteToAdd, ...prevNotes]);
        setNewNote("");
        setShowAddNote(false);

        // Reset consultation date to today for next note
        setConsultationDate(new Date().toISOString().split("T")[0]);

        // Show success message briefly
        setError("");
        setSuccess("Note saved successfully!");

        // Clear success message after 2 seconds
        setTimeout(() => {
          setSuccess("");
        }, 2000);

        // Call onRefresh to ensure data is synced
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setError(data.message || "Failed to add note");
      }
    } catch (error) {
      setError("Error adding note. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReport = async (e) => {
    e.preventDefault();
    if (!reportFile || !reportName.trim() || !reportType.trim()) {
      setUploadError("Please fill all required fields and select a file");
      return;
    }

    setUploadLoading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("reportFile", reportFile);
      formData.append("patientIdentifier", patient.email || patient.phone);
      formData.append("reportName", reportName.trim());
      formData.append("reportType", reportType.trim());
      formData.append("description", reportDescription.trim());
      formData.append("uploadDate", uploadDate);

      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_REPORTS_UPLOAD),
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        // Add the new report to the local state immediately
        setReports([data.report, ...reports]);

        // Reset form
        setReportFile(null);
        setReportName("");
        setReportType("");
        setReportDescription("");
        setUploadDate(new Date().toISOString().split("T")[0]);
        setShowUploadReport(false);

        // Call onRefresh to update parent component with fresh data
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setUploadError(data.message || "Failed to upload report");
      }
    } catch (error) {
      setUploadError("Error uploading report. Please try again.");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadReport = async (timestamp, reportName) => {
    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_REPORTS_DOWNLOAD) +
          "/" +
          timestamp
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = reportName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to download report");
      }
    } catch (error) {
      alert("Error downloading report. Please try again.");
    }
  };

  const handleDeleteReport = async (timestamp) => {
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_REPORTS_DELETE) +
          "/" +
          timestamp,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove the report from local state immediately
        setReports(reports.filter((report) => report.Timestamp !== timestamp));

        // Refresh parent component if callback exists
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert(data.message || "Failed to delete report");
      }
    } catch (error) {
      alert("Error deleting report. Please try again.");
    }
  };

  // Use centralized date formatting utility
  const formatDate = formatAppointmentDate;

  const formatTime = (timeString) => {
    if (!timeString) return "TBD";
    return timeString;
  };

  return (
    <motion.div
      className="patient-profile-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="patient-profile-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="patient-profile-header">
          <button className="close-btn" onClick={onClose}>
            <FaArrowLeft />
            Back to Admin
          </button>
          <h2>
            <FaUser /> Patient Profile
          </h2>
        </div>

        <div className="patient-profile-content">
          {/* Patient Basic Information */}
          <div className="patient-info-section">
            <h3>Basic Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <FaUser />
                <div>
                  <label>Name:</label>
                  <span>{patient.name}</span>
                </div>
              </div>
              <div className="info-item">
                <FaEnvelope />
                <div>
                  <label>Email:</label>
                  <span>{patient.email}</span>
                </div>
              </div>
              <div className="info-item">
                <FaPhone />
                <div>
                  <label>Phone:</label>
                  <span>{patient.phone}</span>
                </div>
              </div>
              <div className="info-item">
                <FaUser />
                <div>
                  <label>Age:</label>
                  <span>{patient.age || "Not provided"}</span>
                </div>
              </div>
              <div className="info-item">
                <FaUser />
                <div>
                  <label>Sex:</label>
                  <span>{patient.sex || "Not provided"}</span>
                </div>
              </div>
              <div className="info-item">
                <FaMapMarkerAlt />
                <div>
                  <label>Address:</label>
                  <span>{patient.address || "Not provided"}</span>
                </div>
              </div>
              <div className="info-item">
                <FaBriefcase />
                <div>
                  <label>Occupation:</label>
                  <span>{patient.occupation || "Not provided"}</span>
                </div>
              </div>
              <div className="info-item">
                <FaCalendar />
                <div>
                  <label>Total Visits:</label>
                  <span>{patient.totalAppointments}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Services History */}
          <div className="services-section">
            <h3>
              <FaStethoscope /> Services Used
            </h3>
            <div className="services-list">
              {patient.services && patient.services.length > 0 ? (
                patient.services
                  .filter(
                    (service) =>
                      service &&
                      service.trim() !== "" &&
                      service !== "Manual Entry" &&
                      service !== "General Consultation"
                  )
                  .map((service, index) => (
                    <span key={index} className="service-badge">
                      {service}
                    </span>
                  ))
              ) : (
                <p className="no-services">No services recorded yet.</p>
              )}
            </div>
          </div>

          {/* Appointment History */}
          <div className="appointments-section">
            <h3>
              <FaHistory /> Appointment History
            </h3>
            <div className="appointments-list">
              {patient.appointments && patient.appointments.length > 0 ? (
                patient.appointments.map((apt, index) => (
                  <div key={index} className="appointment-item">
                    <div className="appointment-header">
                      <span className="appointment-date">
                        {formatDate(apt.date)} at {formatTime(apt.time)}
                      </span>
                      <span
                        className={`appointment-status ${
                          apt.status === "Done" ? "done" : "pending"
                        }`}
                      >
                        {apt.status === "Done" ? "✅" : "⏳"}
                      </span>
                    </div>
                    <div className="appointment-details">
                      <span className="service-name">{apt.service}</span>
                      {apt.additionalInfo && (
                        <p className="additional-info">{apt.additionalInfo}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-appointments">No appointments recorded yet.</p>
              )}
            </div>
          </div>

          {/* Medical Notes Section */}
          <div className="notes-section">
            <div className="notes-header">
              <h3>
                <FaNotesMedical /> Medical Notes
              </h3>
              <button
                className="add-note-btn"
                onClick={() => {
                  setShowAddNote(!showAddNote);
                  // Clear any previous messages when opening the form
                  setError("");
                  setSuccess("");
                }}
              >
                <FaPlus /> Add Note
              </button>
            </div>

            {/* Add Note Form */}
            {showAddNote && (
              <motion.form
                className="add-note-form"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                onSubmit={handleAddNote}
              >
                <div className="form-group">
                  <label htmlFor="consultation-date">Consultation Date:</label>
                  <input
                    type="date"
                    id="consultation-date"
                    value={consultationDate}
                    onChange={(e) => setConsultationDate(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="note-content">Note:</label>
                  <textarea
                    id="note-content"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter consultation notes, observations, recommendations..."
                    rows="4"
                    required
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}
                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowAddNote(false)}
                  >
                    <FaTimes /> Cancel
                  </button>
                  <button type="submit" className="save-btn" disabled={loading}>
                    {loading ? (
                      <>
                        <FaSpinner className="spinning" /> Saving...
                      </>
                    ) : (
                      "Save Note"
                    )}
                  </button>
                </div>
              </motion.form>
            )}

            {/* Notes List */}
            <div className="notes-list">
              {notes && notes.length > 0 ? (
                notes.map((note, index) => {
                  // Handle both old and new data formats
                  const consultationDate =
                    note.ConsultationDate || note.consultation_date;
                  const timestamp = note.Timestamp || note.timestamp;
                  const noteContent = note.Note || note.note;

                  return (
                    <div key={index} className="note-item">
                      <div className="note-header">
                        <div className="note-meta">
                          <span className="note-date">
                            {formatDate(consultationDate)}
                          </span>
                        </div>
                        <span className="note-time">
                          {new Date(timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="note-content">{noteContent}</div>
                    </div>
                  );
                })
              ) : (
                <div className="no-notes">
                  <p>No medical notes available for this patient.</p>
                  <p>Click "Add Note" to start documenting consultations.</p>
                </div>
              )}
            </div>
          </div>

          {/* Patient Reports Section */}
          <div className="reports-section">
            <div className="reports-header">
              <h3>
                <FaFileAlt /> Patient Reports
              </h3>
              <button
                className="add-report-btn"
                onClick={() => setShowUploadReport(!showUploadReport)}
              >
                <FaFileUpload /> Upload Report
              </button>
            </div>

            {/* Upload Report Form */}
            {showUploadReport && (
              <motion.form
                className="upload-report-form"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                onSubmit={handleUploadReport}
              >
                <div className="form-group">
                  <label htmlFor="upload-date">Report Date:</label>
                  <input
                    type="date"
                    id="upload-date"
                    value={uploadDate}
                    onChange={(e) => setUploadDate(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="report-name">Report Name:</label>
                  <input
                    type="text"
                    id="report-name"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    placeholder="Enter report name (e.g., ECG Report, Blood Test)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="report-type">Report Type:</label>
                  <select
                    id="report-type"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    required
                  >
                    <option value="">Select report type</option>
                    <option value="Lab Report">Lab Report</option>
                    <option value="Imaging">Imaging (X-ray, CT, MRI)</option>
                    <option value="ECG">ECG</option>
                    <option value="Echo">Echocardiogram</option>
                    <option value="Stress Test">Stress Test</option>
                    <option value="Prescription">Prescription</option>
                    <option value="Discharge Summary">Discharge Summary</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="report-file">Select File:</label>
                  <input
                    type="file"
                    id="report-file"
                    onChange={(e) => setReportFile(e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    required
                  />
                  <small>
                    Accepted formats: PDF, Images (JPG, PNG), Word documents.
                    Max size: 10MB
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="report-description">
                    Description (Optional):
                  </label>
                  <textarea
                    id="report-description"
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder="Additional notes about this report..."
                    rows="3"
                  />
                </div>
                {uploadError && (
                  <div className="error-message">{uploadError}</div>
                )}
                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowUploadReport(false)}
                  >
                    <FaTimes /> Cancel
                  </button>
                  <button
                    type="submit"
                    className="upload-btn"
                    disabled={uploadLoading}
                  >
                    {uploadLoading ? "Uploading..." : "Upload Report"}
                  </button>
                </div>
              </motion.form>
            )}

            {/* Reports List */}
            <div className="reports-list">
              {reports && reports.length > 0 ? (
                reports.map((report, index) => (
                  <div key={index} className="report-item">
                    <div className="report-header">
                      <div className="report-info">
                        <span className="report-name">{report.ReportName}</span>
                        <span className="report-type">{report.ReportType}</span>
                      </div>
                      <div className="report-actions">
                        <button
                          className="download-btn"
                          onClick={() =>
                            handleDownloadReport(
                              report.Timestamp,
                              report.ReportName
                            )
                          }
                          title="Download Report"
                        >
                          <FaDownload />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteReport(report.Timestamp)}
                          title="Delete Report"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="report-meta">
                      <span className="report-date">
                        {formatDate(report.UploadDate)}
                      </span>
                      <span className="report-size">
                        {(report.FileSize / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {report.Description && (
                      <div className="report-description">
                        {report.Description}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-reports">
                  <p>No reports uploaded for this patient.</p>
                  <p>
                    Click "Upload Report" to add medical reports, test results,
                    or documents.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PatientProfile;
